import React, { useState, useEffect, useRef } from "react";
import { Container, Button, Spinner, Alert, Badge } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaTruck } from "react-icons/fa";
import { transportsAPI } from '../../api/apiClient';
import { useTranslation } from "react-i18next";
import Pusher from "pusher-js";

export default function TransportLiveTrack() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [initialPosition, setInitialPosition] = useState(null);
  const [driverData, setDriverData] = useState(null);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerInstanceRef = useRef(null);
  const animationRef = useRef(null); // لتتبع الحركة
  const pusherRef = useRef(null);
  const googleMapsLoadedRef = useRef(false);

  const PUSHER_KEY = "1a6dafa4504c853b7f61";
  const PUSHER_CLUSTER = "mt1";
  const GOOGLE_API_KEY = "AIzaSyD1U0djKe-DQ9ldXwK8Ob-EiiHcxvRH_3Y";

  useEffect(() => {
    if (id) fetchInitialData();
    else {
      setError("Order ID is missing");
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!window.google && !googleMapsLoadedRef.current) loadGoogleMaps();
  }, []);

  useEffect(() => {
    if (initialPosition && window.google && !mapInstanceRef.current) initMap();
  }, [initialPosition]);

  const loadGoogleMaps = () => {
    if (googleMapsLoadedRef.current) return;
    googleMapsLoadedRef.current = true;

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log("✅ Google Maps loaded successfully");
      if (initialPosition) initMap();
    };
    script.onerror = () => setError("Failed to load maps");
    document.head.appendChild(script);
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError("");
      console.log("Fetching data for order ID:", id);

      const response = await transportsAPI.liveTracking(id);
      if (response.data && response.data.data) {
        const data = response.data.data;
        const position = {
          lat: parseFloat(data.lat),
          lng: parseFloat(data.lng),
        };
        setInitialPosition(position);
        setDriverData(data);
        if (window.google) initMap();
        setupPusher();
      } else {
        setError("No tracking data available");
      }
    } catch (err) {
      console.error("Error fetching live track data:", err);
      setError(err.response?.data?.message || t("failed_to_fetch_tracking"));
    } finally {
      setLoading(false);
    }
  };

  const initMap = () => {
    if (!initialPosition || !window.google) return;

    const mapInstance = new window.google.maps.Map(mapRef.current, {
      zoom: 16,
      center: initialPosition,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });

    const driverIcon = {
      url: "https://img.icons8.com/external-soft-fill-juicy-fish/40/external-lorry-vehicles-soft-fill-soft-fill-juicy-fish-3.png",
      scaledSize: new window.google.maps.Size(48, 48),
      anchor: new window.google.maps.Point(24, 48),
    };

    const markerInstance = new window.google.maps.Marker({
      position: initialPosition,
      map: mapInstance,
      icon: driverIcon,
      title: "Driver Location",
    });

    mapInstanceRef.current = mapInstance;
    markerInstanceRef.current = markerInstance;

    console.log("✅ Map and marker initialized successfully");
  };

  const setupPusher = () => {
    if (!id) return;
    try {
      if (pusherRef.current) pusherRef.current.disconnect();

      const pusherInstance = new Pusher(PUSHER_KEY, {
        cluster: PUSHER_CLUSTER,
        forceTLS: true,
      });

      const channel = pusherInstance.subscribe(`deliveries.${id}`);

      channel.bind("booking-location-updated", (data) => {
        if (data && data.lat && data.lng) {
          const newPosition = {
            lat: parseFloat(data.lat),
            lng: parseFloat(data.lng),
          };
          smoothMoveTo(newPosition); // 👈 التحريك التدريجي
        }
      });

      pusherRef.current = pusherInstance;
    } catch (err) {
      console.error("Error setting up Pusher:", err);
    }
  };

  // ✅ الحركة السلسة للماركر
  const smoothMoveTo = (newPosition) => {
    const marker = markerInstanceRef.current;
    const map = mapInstanceRef.current;
    if (!marker || !map) return;

    const oldPos = marker.getPosition();
    const oldLat = oldPos.lat();
    const oldLng = oldPos.lng();

    const deltaLat = newPosition.lat - oldLat;
    const deltaLng = newPosition.lng - oldLng;

    const steps = 50; // كل تحديث = جزء صغير (كل 20ms → 1 ثانية تقريبًا)
    let currentStep = 0;

    if (animationRef.current) clearInterval(animationRef.current);

    animationRef.current = setInterval(() => {
      currentStep++;
      const lat = oldLat + (deltaLat * currentStep) / steps;
      const lng = oldLng + (deltaLng * currentStep) / steps;
      const position = new window.google.maps.LatLng(lat, lng);

      marker.setPosition(position);
      map.panTo(position);

      if (currentStep >= steps) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    }, 20); // ← كل 20 ملّي ثانية (تقدر تزودها عشان تبطّأ الحركة)
  };

  useEffect(() => {
    return () => {
      if (pusherRef.current) pusherRef.current.disconnect();
      if (animationRef.current) clearInterval(animationRef.current);
    };
  }, []);

  const handleBack = () => navigate(-1);

  if (loading)
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <Spinner animation="border" variant="warning" />
          <p>{t("loading")}</p>
        </div>
      </Container>
    );

  if (error)
    return (
      <Container className="p-4">
        <Alert variant="danger">
          <h5>{t("error")}</h5>
          {error}
          <div className="mt-3">
            <Button variant="outline-danger" onClick={handleBack}>
              <FaArrowLeft className="me-2" />
              {t("back")}
            </Button>
          </div>
        </Alert>
      </Container>
    );

  return (
    <Container fluid className="p-0 position-relative">
      <div id="map" ref={mapRef} style={{ height: "100vh", width: "100%" }} />

      <div className="position-absolute top-0 start-0 p-3">
        <Button variant="light" onClick={handleBack} className="shadow">
          <FaArrowLeft className="me-2" />
          {t("back")}
        </Button>
      </div>

      <div className="position-absolute bottom-0 start-0 p-3">
        <Badge bg="success" className="p-2 fs-6">
          <FaTruck className="me-2" />
          {t("live_tracking_active")}
        </Badge>
        {driverData && (
          <div className="mt-2 p-2 bg-light rounded">
            <small>Order: {id}</small>
          </div>
        )}
      </div>
    </Container>
  );
}